<template>
  <div
    class="w-full bg-gradient-to-br from-[#eff6ff] via-[#f0f9ff] to-[#f8fafc]"
  >
    <div class="max-w-6xl mx-auto px-4 py-6 sm:px-6">
      <!-- Header Section -->
      <div class="mb-6">
        <p class="text-xs font-bold uppercase tracking-[0.25em] text-[#3b82f6]">
          Badge Explorer
        </p>
        <h1 class="mt-2 text-3xl sm:text-4xl font-bold text-[#111814]">
          Design badges that people want to earn
        </h1>
        <p class="mt-3 text-sm sm:text-base text-[#111814]/60">
          Customize colors, images, and shaders to shape a premium look. Inspire
          your community to join and collect credentials that showcase
          reputation.
        </p>
      </div>

      <!-- Features Grid -->
      <div class="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div class="rounded-2xl border border-black/5 bg-white p-5">
          <p class="text-xs font-bold uppercase tracking-widest text-[#3b82f6]">
            Community
          </p>
          <h3 class="mt-2 text-lg font-bold text-[#111814]">
            Launch your community
          </h3>
          <p class="mt-2 text-sm text-[#111814]/60">
            Create a home for members, highlight their roles, and issue badges
            that drive engagement.
          </p>
        </div>
        <div class="rounded-2xl border border-black/5 bg-white p-5">
          <p class="text-xs font-bold uppercase tracking-widest text-[#3b82f6]">
            Reputation
          </p>
          <h3 class="mt-2 text-lg font-bold text-[#111814]">
            Build brand trust
          </h3>
          <p class="mt-2 text-sm text-[#111814]/60">
            Show verified achievements, transparent participation, and a clear
            reputation layer for your brand.
          </p>
        </div>
        <div
          class="rounded-2xl border border-black/5 bg-white p-5 sm:col-span-2 lg:col-span-1"
        >
          <p class="text-xs font-bold uppercase tracking-widest text-[#3b82f6]">
            Social
          </p>
          <h3 class="mt-2 text-lg font-bold text-[#111814]">Social insights</h3>
          <p class="mt-2 text-sm text-[#111814]/60">
            Go beyond badges with social network analysis and community signals
            to understand impact.
          </p>
        </div>
      </div>

      <!-- Badge Customizer Section -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div class="rounded-2xl border border-black/5 bg-white p-5 sm:p-6">
          <div class="flex flex-col justify-center items-center min-h-[460px]">
            <div
              ref="badgeContainerRef"
              class="scale-[1.12] sm:scale-[1.18] origin-center"
            >
              <TemplatePreview
                :templateImage="selectedImage"
                :templateName="badgeTitle"
                :templateThemeBgColor="bgColor"
                :templateThemeTextColor="textColor"
                :templateThemeEffect="selectedShader"
                :templateThemeTexture="''"
                :templateThemeIsLogoBack="false"
                :issuerName="issuerName"
                :issuerImage="issuerLogo"
                type="Basic"
                :formatSm="false"
                :is3d="true"
              />
            </div>
            <!-- <button
              @click="downloadBadge"
              class="mt-6 flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors shadow-sm text-sm font-medium text-gray-700"
            >
              <i class="fas fa-download text-gray-500"></i>
              Download
            </button> -->
          </div>
        </div>

        <div class="rounded-2xl border border-black/5 bg-white p-6 space-y-6">
          <div>
            <h2 class="text-lg font-bold mb-2">Badge title</h2>
            <input
              v-model="badgeTitle"
              class="w-full rounded-lg border border-black/10 px-3 py-2 text-sm"
              placeholder="My First Badge"
            />
          </div>

          <div>
            <h2 class="text-lg font-bold mb-3">Style</h2>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p
                  class="text-xs font-semibold uppercase tracking-widest text-[#608a72] mb-2"
                >
                  Base Tint
                </p>
                <div class="flex items-center gap-2">
                  <button
                    class="h-9 w-9 rounded-full border border-black/10"
                    :style="{ backgroundColor: bgColor }"
                  ></button>
                  <button
                    class="h-9 w-9 rounded-full border border-black/10 bg-[#3b82f6]"
                    @click="bgColor = '#3b82f6'"
                  ></button>
                  <button
                    class="h-9 w-9 rounded-full border border-black/10 bg-[#1f6f41]"
                    @click="bgColor = '#1f6f41'"
                  ></button>
                  <label
                    class="h-9 w-9 rounded-full border border-dashed border-black/20 flex items-center justify-center cursor-pointer"
                  >
                    <input v-model="bgColor" type="color" class="sr-only" />
                    <span class="text-xs text-[#608a72]">+</span>
                  </label>
                </div>
              </div>
              <div>
                <p
                  class="text-xs font-semibold uppercase tracking-widest text-[#608a72] mb-2"
                >
                  Text Color
                </p>
                <div class="flex items-center gap-2">
                  <button
                    class="h-9 w-9 rounded-full border border-black/10 bg-black"
                    @click="textColor = '#000000'"
                  ></button>
                  <button
                    class="h-9 w-9 rounded-full border border-black/10 bg-white"
                    @click="textColor = '#FFFFFF'"
                  ></button>
                  <label
                    class="h-9 w-9 rounded-full border border-dashed border-black/20 flex items-center justify-center cursor-pointer"
                  >
                    <input v-model="textColor" type="color" class="sr-only" />
                    <span class="text-xs text-[#608a72]">+</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 class="text-lg font-bold mb-2">Image</h2>
            <div class="grid grid-cols-3 gap-3">
              <button
                v-for="(img, idx) in imageOptions"
                :key="idx"
                @click="applyImagePreset(img)"
                class="aspect-square rounded-lg overflow-hidden border"
                :class="
                  selectedImage === img.src
                    ? img.borderClass
                    : 'border-black/10'
                "
              >
                <img
                  :src="img.src"
                  alt="badge option"
                  class="h-full w-full object-cover"
                />
              </button>
            </div>
          </div>

          <div
            class="rounded-2xl bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0c1929] p-4 -mx-1"
          >
            <div class="relative">
              <button
                v-if="canScrollLeft"
                class="hidden sm:flex items-center justify-center absolute left-0 top-[38%] -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-[#1e3a5f]/95 backdrop-blur border border-white/10 shadow-xl shadow-black/30"
                @click="scrollShaders(-1)"
                aria-label="Scroll shaders left"
              >
                <span class="text-sm text-[#3b82f6]">‹</span>
              </button>
              <button
                v-if="canScrollRight"
                class="hidden sm:flex items-center justify-center absolute right-0 top-[38%] -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-[#1e3a5f]/95 backdrop-blur border border-white/10 shadow-xl shadow-black/30"
                @click="scrollShaders(1)"
                aria-label="Scroll shaders right"
              >
                <span class="text-sm text-[#3b82f6]">›</span>
              </button>
              <div
                ref="shaderScroller"
                class="overflow-x-auto py-5 px-12"
                @scroll="updateScrollState"
              >
                <div class="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  <div class="space-y-1 min-w-0">
                    <button
                      class="h-14 w-14 rounded-lg border border-white/10 flex items-center justify-center"
                      :class="
                        selectedShader === 'glare'
                          ? 'ring-2 ring-[#3b82f6]'
                          : ''
                      "
                      :style="{ backgroundColor: bgColor }"
                      @click="selectedShader = 'glare'"
                    >
                    </button>
                    <p class="text-[10px] text-white/50 text-center truncate">
                      Solid color
                    </p>
                  </div>
                  <div
                    v-for="shader in visibleShaderList"
                    :key="shader.id"
                    class="space-y-1 min-w-0"
                  >
                    <button
                      class="h-14 w-14 rounded-lg border border-white/10 bg-cover bg-center"
                      :class="
                        selectedShader === `shader_${shader.id}`
                          ? 'ring-2 ring-[#3b82f6]'
                          : ''
                      "
                      :style="{
                        backgroundImage: `url(${getShaderPreviewUrl(
                          shader.id
                        )})`,
                      }"
                      @click="selectedShader = `shader_${shader.id}`"
                      :title="
                        shader.name_en ||
                        shader.name_fr ||
                        shader.base_color ||
                        `Shader ${shader.id}`
                      "
                    ></button>
                    <p class="text-[10px] text-white/50 text-center truncate">
                      {{
                        shader.name_en ||
                        shader.name_fr ||
                        shader.base_color ||
                        `Shader ${shader.id}`
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex justify-center gap-1.5 mt-2">
              <span
                v-for="i in dashCount"
                :key="i"
                class="h-1.5 rounded-full cursor-pointer transition-all duration-300"
                :class="
                  activeDashIndex === i - 1
                    ? 'w-6 bg-[#3b82f6]/60'
                    : 'w-6 bg-[#3b82f6]/20'
                "
                @click="scrollToDash(i - 1)"
              ></span>
            </div>
            <div
              v-if="shaderList.length > maxVisibleShaders"
              class="mt-3 text-center"
            >
              <a
                href="https://studio.mydid.com/signin"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs font-semibold text-[#3b82f6] hover:text-[#3b82f6]/80 transition-colors"
              >
                Join to see {{ shaderList.length - maxVisibleShaders }} more
                effects &rarr;
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Call to Action Section -->
      <div class="mt-12 mb-6">
        <div
          class="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0c1929] p-6 sm:p-10 lg:p-12"
        >
          <!-- Background decorative elements -->
          <div
            class="absolute top-0 right-0 w-64 h-64 bg-[#3b82f6]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"
          ></div>
          <div
            class="absolute bottom-0 left-0 w-48 h-48 bg-[#3b82f6]/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2"
          ></div>

          <div
            class="relative z-10 flex flex-col lg:flex-row items-center gap-8 lg:gap-12"
          >
            <!-- Text Content -->
            <div class="flex-1 text-center lg:text-left">
              <p
                class="inline-block px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#3b82f6] bg-[#3b82f6]/10 rounded-full mb-4"
              >
                myDid Studio
              </p>
              <h2
                class="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight"
              >
                Ready to create your own badges?
              </h2>
              <p class="mt-4 text-base sm:text-lg text-white/70 max-w-xl">
                Stop dreaming, start building. Join thousands of creators who
                use myDid Studio to design, issue, and manage verifiable
                credentials for their communities.
              </p>
              <ul class="mt-6 space-y-3 text-sm sm:text-base text-white/80">
                <li
                  class="flex items-center justify-center lg:justify-start gap-2"
                >
                  <svg
                    class="w-5 h-5 text-[#3b82f6] flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      class="fill-[#e9ebef]"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span>Create unlimited badge templates</span>
                </li>
                <li
                  class="flex items-center justify-center lg:justify-start gap-2"
                >
                  <svg
                    class="w-5 h-5 text-[#3b82f6] flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      class="fill-[#e9ebef]"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span>Issue verifiable credentials on-chain</span>
                </li>
                <li
                  class="flex items-center justify-center lg:justify-start gap-2"
                >
                  <svg
                    class="w-5 h-5 text-[#3b82f6] flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span>Grow and engage your community</span>
                </li>
              </ul>
            </div>

            <!-- CTA Button Area -->
            <div class="flex flex-col items-center gap-4">
              <a
                href="https://studio.mydid.com/signin"
                target="_blank"
                rel="noopener noreferrer"
                class="group relative inline-flex items-center gap-3 px-8 py-4 sm:px-10 sm:py-5 bg-[#3b82f6] text-white font-bold text-base sm:text-lg rounded-xl shadow-lg shadow-[#3b82f6]/30 hover:shadow-xl hover:shadow-[#3b82f6]/40 hover:scale-105 transition-all duration-300"
              >
                <span>Start Creating Now</span>
                <svg
                  class="w-5 h-5 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </a>
              <p class="text-xs text-white/50">
                Free to get started • No credit card required
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TemplatePreview from '@/components/TemplatePreview.vue';
import shaderList from '../../assets/shaderList.json';
import html2canvas from 'html2canvas';
import { createToast } from 'mosha-vue-toastify';

export default {
  name: 'BadgeCustomizer',
  components: { TemplatePreview },
  data() {
    return {
      badgeTitle: 'My First Badge',
      issuerName: 'myDid',
      issuerLogo: require('/assets/images/logo_comect.png'),
      bgColor: '#c92222',
      textColor: '#FFFFFF',
      selectedShader: 'shader_7',
      imageOptions: [
        {
          src: require('/assets/images/dog-hero.png'),
          bgColor: '#c92222',
          textColor: '#FFFFFF',
          shaderName: 'Carbon',
          borderClass: 'border-[#c92222]',
        },
        {
          src: require('/assets/images/eSport.png'),
          bgColor: '#4953df',
          textColor: '#FFFFFF',
          shaderName: 'Shooting Stars',
          borderClass: 'border-[#4953df]',
        },
        {
          src: require('/assets/images/vp_Ticket.png'),
          bgColor: '#f5c951',
          textColor: '#FFFFFF',
          shaderName: 'ASCII',
          borderClass: 'border-[#f5c951]',
        },
      ],
      selectedImage: require('/assets/images/dog-hero.png'),
      shaderList,
      maxVisibleShaders: 5,
      canScrollLeft: false,
      canScrollRight: false,
      activeDashIndex: 0,
      dashCount: 3,
    };
  },
  computed: {
    visibleShaderList() {
      return this.shaderList.slice(0, this.maxVisibleShaders);
    },
    shaderPreviewBaseUrl() {
      const shaderIpfsFolderId =
        process.env.VUE_APP_SHADER_IPFS_FOLDER_ID || '';
      return shaderIpfsFolderId
        ? `https://myntfsid.mypinata.cloud/ipfs/${shaderIpfsFolderId}`
        : 'https://myntfsid.mypinata.cloud/ipfs';
    },
  },
  methods: {
    applyImagePreset(img) {
      this.selectedImage = img.src;
      if (img.bgColor) this.bgColor = img.bgColor;
      if (img.textColor) this.textColor = img.textColor;
      if (img.shaderName) {
        const match = this.shaderList.find((shader) => {
          const name =
            shader.name_en || shader.name_fr || `Shader ${shader.id}`;
          return name.toLowerCase() === img.shaderName.toLowerCase();
        });
        if (match) {
          this.selectedShader = `shader_${match.id}`;
        }
      }
    },
    getShaderPreviewUrl(id) {
      return `${this.shaderPreviewBaseUrl}/shader_${id}.png`;
    },
    scrollShaders(direction) {
      const el = this.$refs.shaderScroller;
      if (!el) return;
      const delta = 200 * direction;
      el.scrollBy({ left: delta, behavior: 'smooth' });
    },
    updateScrollState() {
      const el = this.$refs.shaderScroller;
      if (!el) return;
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      this.canScrollLeft = el.scrollLeft > 4;
      this.canScrollRight = el.scrollLeft < maxScrollLeft - 4;

      // Update dash count and active index based on scroll position
      if (maxScrollLeft > 0) {
        const pageWidth = el.clientWidth;
        this.dashCount = Math.max(1, Math.ceil(el.scrollWidth / pageWidth));
        const progress = el.scrollLeft / maxScrollLeft;
        this.activeDashIndex = Math.min(
          Math.round(progress * (this.dashCount - 1)),
          this.dashCount - 1
        );
      } else {
        this.dashCount = 1;
        this.activeDashIndex = 0;
      }
    },
    scrollToDash(index) {
      const el = this.$refs.shaderScroller;
      if (!el) return;
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      const targetScroll =
        this.dashCount > 1 ? (index / (this.dashCount - 1)) * maxScrollLeft : 0;
      el.scrollTo({ left: targetScroll, behavior: 'smooth' });
    },
    async downloadBadge() {
      try {
        const container = this.$refs.badgeContainerRef;
        if (!container) {
          throw new Error('Badge container not found');
        }
        const canvas = await html2canvas(container, {
          useCORS: true,
          allowTaint: true,
          scale: 2,
          backgroundColor: null,
          logging: false,
        });
        const dataUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `${this.badgeTitle.replace(
          /[^a-z0-9]/gi,
          '_'
        )}_${new Date().toISOString().slice(0, 10)}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error('Capture failed:', err);
        createToast('Failed to download badge image', {
          type: 'danger',
          showIcon: true,
          position: 'top-right',
          toastBackgroundColor: '#050931',
        });
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateScrollState();
    });
  },
};
</script>
